/**
 * Synchronisation du snapshot vers Supabase (§9, étape 10) — la première brique de la
 * boucle virale : sans ville en ligne, il n'y a rien à visiter, donc rien à partager.
 *
 * ── Principes ──────────────────────────────────────────────────────────────
 *
 * **Le jeu ne dépend JAMAIS du réseau.** La partie vit en localStorage ; la synchro est
 * une ombre portée en arrière-plan. Toute erreur — hors ligne, quota, panne — dégrade
 * en silence et réessaie plus tard. Un idle qui bloque sur un `fetch` est cassé.
 *
 * **Un joueur = un `auth.uid()`, même sans compte.** La première synchro déclenche une
 * connexion anonyme Supabase : la policy RLS reste `owner_id = auth.uid()`, et
 * l'« upgrade » vers un compte e-mail ou Google est natif (`updateUser`,
 * `linkIdentity`) — aucune migration de données le jour où le joueur s'attache.
 *
 * **Le slug vient du serveur.** Le client n'a pas le privilège d'écrire cette colonne
 * (§13) : l'INSERT le laisse au DEFAULT, le SELECT le rapporte.
 *
 * **L'écriture est espacée.** Un push par minute quand quelque chose a changé, plus un
 * push garanti quand l'onglet se cache — même politique que la sauvegarde locale, aux
 * proportions du réseau. Le snapshot pèse quelques dizaines de Ko : inutile de pousser
 * à chaque tick ce que personne ne lit plus d'une fois par minute.
 */

import { ensureAuthSession, getSupabase, isSupabaseConfigured } from './supabase';
import type { CityClientInsert, CityClientUpdate } from './supabase';
import type { CitySnapshot } from '@/core/gameState';

/** Push au plus toutes les 60 s : la page publique n'est pas un flux temps réel. */
export const SYNC_INTERVAL_MS = 60_000;

/** Ce que le jeu connaît de sa ville distante. */
export interface RemoteCity {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
}

export interface SyncOptions {
  readonly getSnapshot: () => CitySnapshot;
  /** Nom par défaut de la ville, déjà localisé — `net` ne compose pas de texte. */
  readonly defaultName: string;
  /** Appelé dès que la ville distante est connue (création ou retrouvaille). */
  readonly onCity?: (city: RemoteCity) => void;
  /**
   * La ville MÉRITE-T-ELLE d'exister côté serveur ?
   *
   * ══ Pourquoi cette question n'était pas posée, et ce que ça coûtait ══════
   *
   * ⚠️ La première synchro partait **quatre secondes après le chargement, sans aucune
   * condition**. Chaque navigateur qui ouvrait le jeu ouvrait donc une session anonyme
   * et créait une ville distante — y compris s'il repartait aussitôt, y compris s'il
   * n'avait rien posé, y compris s'il s'agissait d'une sonde.
   *
   * Mesuré le 21 août : **1 634 villes**, dont dix seulement avaient vécu plus de cinq
   * minutes. ~380 nouvelles par jour. Le job de purge (ROADMAP 0.4) a été écrit pour
   * nettoyer ça — mais nettoyer après coup une chose qu'on n'aurait pas dû créer est le
   * mauvais bout du problème.
   *
   * Le prédicat est fourni par l'appelant : `src/net` n'a pas à savoir ce qui rend une
   * ville digne d'un compte. Il sait seulement qu'il ne crée rien tant qu'on ne lui dit
   * pas oui.
   *
   * ⚠️ Il ne garde QUE la création. Une ville qui existe déjà continue de se
   * synchroniser toujours — sinon un joueur qui vend son dernier bâtiment verrait sa
   * sauvegarde distante se figer, ce qui serait bien pire que le bruit qu'on évite.
   */
  readonly worthCreating?: () => boolean;
}

export interface SyncHandle {
  /** Ville distante, ou `null` tant que la première synchro n'a pas abouti. */
  city(): RemoteCity | null;
  /** Pousse immédiatement si quelque chose a changé. */
  flush(): void;
  /**
   * Renomme la ville. `name` fait partie des deux seules colonnes client-écrivables
   * (§5) : c'est l'identité du joueur, pas une donnée de classement. Rend la ville mise
   * à jour, ou `null` si le renommage n'a pas abouti (hors-ligne, pas encore de ville).
   */
  rename(name: string): Promise<RemoteCity | null>;
  destroy(): void;
}

/**
 * Démarre la synchronisation d'arrière-plan.
 *
 * L'appelant n'attend rien : tout est asynchrone et silencieux. Sans configuration
 * Supabase, l'objet rendu ne fait rien du tout — le jeu local reste entier.
 */
export function createSync(options: SyncOptions): SyncHandle {
  if (!isSupabaseConfigured()) {
    return {
      city: () => null,
      flush: () => undefined,
      rename: () => Promise.resolve(null),
      destroy: () => undefined,
    };
  }

  /**
   * Le slug est mémorisé LOCALEMENT dès qu'il est connu.
   *
   * La page publique doit pouvoir écarter une auto-visite sans ouvrir de session : elle
   * n'a ni compte ni requête à sa disposition, seulement le stockage local. Sans ce
   * rangement, le joueur qui ouvre le lien de sa propre ville pour le vérifier
   * s'enverrait un boost à lui-même — exactement l'abus que le §9 nomme en premier.
   */
  const remember = (city: RemoteCity): void => {
    try {
      localStorage.setItem('villopia:city:slug', city.slug);
    } catch {
      // Stockage indisponible : l'auto-visite reste écartée par la contrainte
      // `visits_not_self` de la base et par l'unicité quotidienne.
    }
  };

  let remote: RemoteCity | null = null;
  let lastPushed = '';
  let timer = 0;
  /** Relance courte tant que la ville n'a pas de quoi exister. */
  let waiting: ReturnType<typeof setTimeout> | undefined;
  let destroyed = false;
  /** Une seule initialisation à la fois : deux `ensureCity` concurrents créeraient
   *  deux INSERT, dont un échouerait sur l'index unique — du bruit pour rien. */
  let initializing: Promise<RemoteCity | null> | null = null;

  /**
   * La ville du joueur, SANS jamais nommer `owner_id`.
   *
   * ══ Pourquoi une fonction serveur pour un filtre trivial ═════════════════
   *
   * C'était `.eq('owner_id', uid)`. Or `owner_id` est l'identifiant qui relie une
   * ville à un compte : il permet de corréler deux villes au même joueur, et le §13
   * n'autorise que le transit d'un pseudonyme. Il doit donc cesser d'être lisible
   * par le client — ce que fait la migration `restreindre_lecture_cities`.
   *
   * Et PostgreSQL exige le privilège de lecture pour **toute colonne référencée par
   * une requête cliente, `where` compris**. Tant que ce filtre vivait ici, la
   * migration cassait la synchronisation de tous les joueurs. `my_city()`, en
   * `security definer`, fait le filtre côté serveur : le client ne nomme plus la
   * colonne, et le privilège peut partir.
   *
   * ⚠️ **Cette fonction doit être EN LIGNE avant que la migration ne joue.** Le
   * service worker sert le jeu en cache : un joueur revenant avec un bundle plus
   * ancien filtrerait encore sur `owner_id`, et sa sauvegarde échouerait jusqu'à ce
   * qu'il recharge. L'ordre n'est pas une précaution, c'est une condition.
   *
   * Rend `null` si la ville n'existe pas encore — l'appelant la crée alors.
   */
  async function findOwnCity(): Promise<RemoteCity | null> {
    const { data, error } = await getSupabase().rpc('my_city');
    if (error !== null) return null;
    // La fonction rend une TABLE, donc zéro ou une ligne. Une ville par joueur est
    // garanti par l'index unique partiel sur `owner_id` (étape 2) ; on ne prend la
    // première que pour satisfaire le type, pas parce qu'il pourrait y en avoir deux.
    return data[0] ?? null;
  }

  async function ensureCity(): Promise<RemoteCity | null> {
    if (remote !== null) return remote;

    const uid = await ensureAuthSession();
    if (uid === null || destroyed) return null;

    const supabase = getSupabase();

    // Retrouvailles d'abord : la ville existe si ce navigateur a déjà synchronisé.
    const found = await findOwnCity();

    if (found !== null) {
      remote = found;
      remember(found);
      options.onCity?.(found);
      return found;
    }

    // Création. Le slug reste au DEFAULT serveur — le client n'a pas le droit de
    // l'écrire, et c'est ce qui garantit qu'aucune insulte n'est représentable (§13).
    const insert: CityClientInsert = {
      owner_id: uid,
      name: options.defaultName,
      snapshot: JSON.parse(JSON.stringify(options.getSnapshot())) as never,
    };

    const { data: created, error } = await supabase
      .from('cities')
      .insert(insert)
      .select('id, slug, name')
      .single();

    if (error !== null) {
      // 23505 : un autre onglet a créé la ville entre notre SELECT et notre INSERT.
      // On la relit plutôt que d'échouer — c'est le même joueur.
      const raced = await findOwnCity();
      if (raced !== null) {
        remote = raced;
        remember(raced);
        options.onCity?.(raced);
      }
      return remote;
    }

    remote = created;
    remember(created);
    options.onCity?.(created);
    return created;
  }

  async function push(): Promise<void> {
    /*
      Le verrou de CRÉATION. Une fois `remote` connu, on ne repose plus la question :
      la ville existe, elle se synchronise, quoi qu'il arrive à son contenu.
    */
    if (remote === null && options.worthCreating?.() === false) {
      /*
        ── ON REVIENT VITE, PAS DANS UNE MINUTE ─────────────────────────────

        Le cycle normal est d'une minute. Un joueur qui pose son premier bâtiment à la
        cinquième seconde attendrait donc **cinquante-cinq secondes** avant que sa ville
        existe côté serveur — et pendant tout ce temps, la section Partage n'a pas
        d'adresse à lui montrer. Mesuré par `probe:sync`, qui a d'abord cru à un verrou
        bloqué alors qu'il était seulement lent.

        Tant qu'il n'y a rien à créer, on repasse toutes les cinq secondes. C'est
        gratuit : le prédicat lit un tableau en mémoire, aucune requête ne part.
      */
      globalThis.clearTimeout(waiting);
      waiting = globalThis.setTimeout(safePush, 5_000);
      return;
    }

    const city = await (initializing ??= ensureCity().finally(() => {
      initializing = null;
    }));
    if (city === null || destroyed) return;

    const snapshot = options.getSnapshot();
    const serialized = JSON.stringify(snapshot);
    // Rien n'a changé depuis le dernier push : ne rien envoyer. La comparaison sur la
    // chaîne évite un aller-retour réseau par minute pour un état identique.
    if (serialized === lastPushed) return;

    const update: CityClientUpdate = {
      snapshot: JSON.parse(serialized) as never,
    };

    const { error } = await getSupabase().from('cities').update(update).eq('id', city.id);
    if (error === null) {
      lastPushed = serialized;
    }
  }

  // Déclaré avant `push` l'utilise : la fonction est hissée, la référence tient.
  const safePush = (): void => {
    // `void` + catch : une synchro qui échoue réessaiera au prochain intervalle,
    // sans jamais remonter jusqu'au joueur.
    push().catch(() => undefined);
  };

  const onHidden = (): void => {
    if (document.visibilityState === 'hidden') safePush();
  };

  // Première synchro douce : après le premier rendu, jamais avant — le §1 impose un
  // jeu jouable en moins de 5 s, et l'auth anonyme n'a rien d'urgent.
  const startDelay = globalThis.setTimeout(safePush, 4_000);
  timer = window.setInterval(safePush, SYNC_INTERVAL_MS);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', safePush);

  return {
    city: () => remote,
    flush: safePush,
    async rename(name) {
      const city = remote;
      const trimmed = name.trim();
      if (city === null || trimmed === '' || trimmed.length > 40) return null;

      const update: CityClientUpdate = { name: trimmed };
      const { error } = await getSupabase().from('cities').update(update).eq('id', city.id);
      if (error !== null) return null;

      remote = { ...city, name: trimmed };
      // Les écrans qui affichent le nom (Social, Ville) se resynchronisent par le même
      // canal que la découverte initiale : une seule voie, pas deux à faire diverger.
      options.onCity?.(remote);
      return remote;
    },
    destroy() {
      destroyed = true;
      globalThis.clearTimeout(startDelay);
      globalThis.clearTimeout(waiting);
      globalThis.clearInterval(timer);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', safePush);
    },
  };
}
